import React, { useEffect, useRef } from "react";
import ascii from "../data/heroAscii.json";
import glyphIndex from "../lib/asciiGlyphs";

const ACCENT = {
  dark: { r: 107, g: 142, b: 232 },
  light: { r: 43, g: 80, b: 184 },
};
const CHARS = ascii.characters;
const PAD = 4;
const FRAME_MS = 1000 / 30;
const DAMPING = 0.93;

// Background code and pointer ripples stay separate from the native video.
const AsciiFluidSimulation = ({ theme = "dark" }) => {
  const canvasRef = useRef(null);
  const clockRef = useRef({ elapsed: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return undefined;

    const accent = ACCENT[theme] || ACCENT.dark;
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const clock = clockRef.current;
    let reducedMotion = motionQuery.matches;
    let disposed = false;
    let inView = true;
    let rafId = null;
    let lastTime = performance.now();
    let bgCanvas = null;
    const state = { w: 0, h: 0, cols: 0, rows: 0, cell: 12, front: [], back: [], speeds: [], offsets: [], streams: [] };
    let lastStream = null;

    function renderBackground() {
      bgCanvas = document.createElement("canvas");
      bgCanvas.width = state.w;
      bgCanvas.height = state.h;
      const bg = bgCanvas.getContext("2d");
      if (!bg) {
        bgCanvas = null;
        return;
      }
      bg.font = '10px "Courier New", monospace';
      bg.fillStyle = theme === "light" ? "rgba(43,80,184,0.13)" : "rgba(139,168,240,0.12)";
      for (let y = 6; y < state.h; y += 18) {
        for (let x = 6; x < state.w; x += 18) bg.fillText("·", x, y);
      }
    }

    function resize() {
      if (disposed) return;
      const rect = canvas.parentElement.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      const media = canvas.closest(".hero-assistant")?.querySelector(".assistant-video-media");
      const mediaRect = media?.getBoundingClientRect();
      // The square video uses object-fit: contain. Match both the glyph spacing
      // and nominal Courier font size after the encoded video is scaled by CSS.
      const videoSide = mediaRect ? Math.min(mediaRect.width, mediaRect.height) : 0;
      const cell = videoSide > 0
        ? Math.max(2, Math.round(videoSide / ascii.gridColumns * 1000) / 1000)
        : width < 768 ? 9 : 12;
      if (state.w !== width || state.h !== height || state.cell !== cell) {
        state.w = width;
        state.h = height;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = Math.round(width * dpr);
        canvas.height = Math.round(height * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        state.cell = cell;
        state.cols = Math.ceil(width / state.cell) + PAD * 2;
        state.rows = Math.ceil(height / state.cell) + PAD * 2;
        state.front = new Float32Array(state.cols * state.rows);
        state.back = new Float32Array(state.front.length);
        state.speeds = Float32Array.from({ length: state.cols }, (_, col) => 3 + (col * 13 % 11) * 0.7);
        state.offsets = Float32Array.from({ length: state.cols }, (_, col) => col * 19 % state.rows);
        state.streams = [];
        lastStream = null;
        const fontSize = videoSide > 0 ? cell * ascii.atlasFontSize / ascii.atlasTileSize : cell;
        ctx.font = `bold ${fontSize}px "Courier New", monospace`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        renderBackground();
      }
      syncPlayback();
    }

    function drawRain() {
      const { cell, cols, rows } = state;
      const time = reducedMotion ? 0 : clock.elapsed;
      // Sparse columns leave copy readable; the face has its own dense stream.
      for (let col = PAD; col < cols - PAD; col += 2) {
        const head = (time * state.speeds[col] + state.offsets[col]) % (rows + 12);
        for (let tail = 0; tail < 12; tail++) {
          const row = Math.floor(head - tail);
          if (row < PAD || row >= rows - PAD) continue;
          const char = glyphIndex(col, row - Math.floor(time * state.speeds[col]), CHARS.length);
          const opacity = (1 - tail / 12) * (theme === "light" ? 0.12 : 0.19);
          ctx.fillStyle = `rgba(${accent.r},${accent.g},${accent.b},${opacity})`;
          ctx.fillText(CHARS[char], (col - PAD + 0.5) * cell, (row - PAD + 0.5) * cell);
        }
      }
    }

    function solveWave() {
      const { front, back, cols, rows } = state;
      for (let y = 1; y < rows - 1; y++) {
        for (let x = 1; x < cols - 1; x++) {
          const i = y * cols + x;
          front[i] = ((back[i - 1] + back[i + 1] + back[i - cols] + back[i + cols]) / 2 - front[i]) * DAMPING;
        }
      }
      state.front = back;
      state.back = front;
    }

    function drawWave() {
      const { cell, front, cols } = state;
      for (let i = 0; i < front.length; i++) {
        const heat = Math.abs(front[i]);
        if (heat < 0.08) continue;
        const col = i % cols;
        const row = Math.floor(i / cols);
        const flow = clock.elapsed * (3 + col % 4 * 0.2);
        const char = glyphIndex(col, row - Math.floor(flow), CHARS.length);
        ctx.fillStyle = `rgba(${accent.r},${accent.g},${accent.b},${Math.min(0.65, heat)})`;
        ctx.fillText(CHARS[char], (col - PAD + 0.5) * cell, (row - PAD + 0.5 + flow % 1) * cell);
      }
    }

    function drawPointerRain() {
      const { cell } = state;
      state.streams = state.streams.filter((stream) => clock.elapsed - stream.born < 2);
      for (const stream of state.streams) {
        const age = clock.elapsed - stream.born;
        const head = stream.y + age * stream.speed;
        const fade = Math.min(1, age / 0.06) * Math.min(1, (2 - age) / 0.6);
        // Each emitted character really travels down the screen, independent
        // of the ripple grid. Bounded trails fade after the pointer has moved.
        for (let tail = 0; tail < 14; tail++) {
          const y = head - tail * cell;
          if (y < 0 || y > state.h) continue;
          const opacity = (1 - tail / 14) * fade * 0.85;
          ctx.fillStyle = `rgba(${accent.r},${accent.g},${accent.b},${opacity})`;
          ctx.fillText(CHARS[glyphIndex(stream.seed, tail, CHARS.length)], stream.x, y);
        }
      }
    }

    function drawFrame(dt) {
      if (!reducedMotion) {
        clock.elapsed += dt;
        if (dt > 0) solveWave();
      }
      ctx.clearRect(0, 0, state.w, state.h);
      if (bgCanvas) ctx.drawImage(bgCanvas, 0, 0);
      drawRain();
      if (!reducedMotion) {
        drawWave();
        drawPointerRain();
      }
    }

    function schedule() {
      if (!disposed && inView && !document.hidden && !reducedMotion && rafId === null) {
        rafId = requestAnimationFrame(loop);
      }
    }

    function loop(now) {
      rafId = null;
      if (disposed || !inView || document.hidden || reducedMotion) return;
      if (now - lastTime >= FRAME_MS) {
        const dt = Math.min(0.12, (now - lastTime) / 1000);
        lastTime = now;
        drawFrame(dt);
      }
      schedule();
    }

    function syncPlayback() {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = null;
      lastTime = performance.now();
      if (!disposed && inView && !document.hidden) {
        drawFrame(0);
        schedule();
      }
    }

    function changeMotionPreference() {
      reducedMotion = motionQuery.matches;
      state.streams = [];
      lastStream = null;
      syncPlayback();
    }

    function splash(event, radius, force) {
      if (disposed || reducedMotion || !inView || document.hidden) return;
      if (event.target instanceof Element && event.target.closest("button, a, input, textarea, select, [role='button']")) return;
      const rect = canvas.getBoundingClientRect();
      const px = event.clientX - rect.left;
      const py = event.clientY - rect.top;
      if (px < 0 || py < 0 || px >= rect.width || py >= rect.height) return;
      const cx = Math.floor(px / state.cell) + PAD;
      const cy = Math.floor(py / state.cell) + PAD;
      if (!lastStream || clock.elapsed - lastStream.born > 0.045 || Math.hypot(px - lastStream.x, py - lastStream.y) > state.cell * 2) {
        lastStream = { x: px, y: py, born: clock.elapsed };
        state.streams.push({ ...lastStream, speed: 75 + cx % 7 * 10, seed: Math.floor(Math.random() * 0x100000000) });
        if (state.streams.length > 72) state.streams.shift();
      }
      for (let y = -radius; y <= radius; y++) {
        for (let x = -radius; x <= radius; x++) {
          const distance = Math.hypot(x, y);
          const col = cx + x;
          const row = cy + y;
          if (distance < radius && col > 0 && col < state.cols - 1 && row > 0 && row < state.rows - 1) {
            state.back[row * state.cols + col] += (1 - distance / radius) * force;
          }
        }
      }
    }

    const move = (event) => splash(event, 3, 0.5);
    const click = (event) => splash(event, 6, 2);
    const observer = typeof IntersectionObserver === "undefined" ? null : new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting;
      syncPlayback();
    });
    const resizeObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(resize);

    resize();
    observer?.observe(canvas);
    resizeObserver?.observe(canvas.parentElement);
    const videoStage = canvas.closest(".hero-assistant")?.querySelector(".assistant-video-stage");
    if (videoStage) resizeObserver?.observe(videoStage);
    window.addEventListener("resize", resize);
    window.addEventListener("pointermove", move, { passive: true });
    window.addEventListener("pointerdown", click, { passive: true });
    document.addEventListener("visibilitychange", syncPlayback);
    motionQuery.addEventListener("change", changeMotionPreference);

    return () => {
      disposed = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      observer?.disconnect();
      resizeObserver?.disconnect();
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerdown", click);
      document.removeEventListener("visibilitychange", syncPlayback);
      motionQuery.removeEventListener("change", changeMotionPreference);
    };
  }, [theme]);

  return (
    <canvas
      ref={canvasRef}
      className="ascii-fluid-sim"
      aria-hidden="true"
      style={{ display: "block", width: "100%", height: "100%", pointerEvents: "none" }}
    />
  );
};

export default AsciiFluidSimulation;
